/**
 * AURA v2 Sidebar — rewritten Apr 22, 2026 (Phase 2 UI rebuild).
 *
 * Design contract (locked in docs/ui-rebuild/DECISIONS.md):
 *   - D-07  Admin keeps 9 core items.
 *   - D-09  Tenants are data-only. Zero hardcoded tenant slugs/IDs.
 *   - D-11  Each role gets a fixed Core (6–10) + collapsible "See more" (3–6).
 *
 * Role resolution flow:
 *   - Super-admin (`admin@aura.ardhanex.com`) sees Super-Admin sidebar.
 *   - Tenant admin (role.name === 'admin' + tenant bound) sees Owner Core.
 *   - Everybody else → resolveRoleKey(user) → matching role config.
 *   - Admin topbar can temp-override with a dept-dropdown (sessionStorage).
 *
 * See-more drawer state persists in localStorage per role (`aura-sidebar-more-<role>`).
 */
import { useEffect, useMemo, useState } from 'react';
import { asLogisticsRole } from '@/lib/roleAliases';
import { NavLink } from 'react-router-dom';
import {
  Activity, AlertTriangle, Archive, ArrowDownToLine, ArrowRightLeft, ArrowUpFromLine, BadgeIndianRupee, Ban, Banknote, BarChart3, BookOpen, Bot, Box, Briefcase, Building2, Calculator, CalendarDays, Camera, CheckCircle, ChevronDown, ChevronRight, ClipboardCheck, ClipboardList, Clock, Coins, CreditCard, Crown, Download, Factory, FileOutput, FileSearch, FileSignature, FileText, FileX2, FlaskConical, FolderKanban, Gauge, Gift, Globe, Hammer, Handshake, Headphones, Inbox, IndianRupee, LayoutDashboard, LifeBuoy, Link2, ListTodo, MapPin, Package, PackageCheck, PackageX, Percent, Phone, Plug, Receipt, Route, Scale, ScanBarcode, ScrollText, Server, Settings, Shield, ShieldCheck, Ship, ShoppingCart, Smartphone, Sparkles, Tags, Target, TrendingUp, Truck, UserCog, Users, Wallet, Warehouse, Workflow, Wrench,
  GitCommitHorizontal, GraduationCap,
} from 'lucide-react';
import { useAuthStore } from '../../stores/auth';
import { api } from '@/utils/api';
import { resolveRoleKey, type DashboardRoleKey } from '../../pages/dashboard/roleLayouts';
import { useFieldProgramConfig } from '../../hooks/useFieldProgramConfig';
import { VL } from '../../pages/veda-agro/vedaLabels';
import { shouldBadgeDealDesk } from './dealDeskBadge';
import { buildScopedSections } from './navCatalog';
import { applyVedaLeadsNav, canSeeLeadTriage, LEAD_TRIAGE_PATH, LEAD_TRIAGE_SUMMARY_URL } from './vedaLeadsNav';
import { brandTag } from '@/lib/brand';

/* ------------------------------------------------------------------ */
/*  Types                                                               */
/* ------------------------------------------------------------------ */

type Icon = typeof LayoutDashboard;

interface NavItem {
  to: string;
  icon: Icon;
  label: string;
  /** Module key used for tenant-level feature flags */
  module?: string;
  /** Inline count badge (optional, future use) */
  count?: number;
  /** 'alert' paints the count badge red (Lead Triage unassigned count). */
  countTone?: 'alert';
  /** Shown only if user.role.name matches (legacy gate — e.g. 'telecaller', 'admin') */
  roleNamesOnly?: string[];
  /**
   * Plan tier gate — item is hidden for tenants below this tier.
   * Supported values: 'ENTERPRISE' | 'GROWTH' | 'STARTER'.
   * Checked against tenant.planTier in filterItems.
   */
  planGate?: 'ENTERPRISE' | 'GROWTH' | 'STARTER';
}

interface NavSection {
  label: string;
  items: NavItem[];
  /** Items in the expandable "See more" drawer (D-11) */
  seeMore?: NavItem[];
}

export type SidebarView =
  | 'owner'
  | 'sales'
  | 'accountant'
  | 'production'
  | 'inventory'
  | 'procurement'
  | 'project'
  | 'service'
  | 'telecaller'
  | 'hr'
  | 'lead_manager'
  | 'lead_triage'
  | 'super_admin'
  // Executive views (Apr 22, 2026 exec sprint — trimmed 3–5-item Core)
  | 'sales_executive'
  | 'service_technician'
  | 'production_operator'
  | 'warehouse_executive'
  | 'accounts_executive'
  | 'field_partner'
  // Apr 26, 2026 (Squad-B) — bifurcation re-introduced. Field Executive sees
  // own/assigned leads only; Backend Staff (tele sales) sees their team's
  // leads. The legacy `sales_executive` view is preserved for back-compat.
  | 'field_executive'
  | 'backend_staff'
  // Veda REV3 — Purchase Manager: scoped to commodity-deal sourcing + masters
  // + HRMS self-service + support. No generic AURA modules (CRM/Finance/Ops).
  | 'purchase'
  // Task 7 (2026-06-19) — Veda Agro wide-bundle manager roles.
  // sales_manager lands on /veda-agro/sales (Deal Desk / Sales Manager Dashboard).
  // logistics_manager lands on /veda-agro/logistics (Logistics Cockpit).
  | 'veda_sales_manager'
  | 'veda_logistics_manager'
  | 'veda_purchase_head'
  // V2 (2026-07-16) — Veda commodity sales rep (role `sales_rep`).
  | 'veda_sales_executive';

const VIEW_LABELS: Record<SidebarView, string> = {
  owner: 'Owner / Admin',
  sales: 'Sales Manager',
  accountant: 'Accountant',
  production: 'Production',
  inventory: 'Inventory',
  procurement: 'Procurement',
  project: 'Projects',
  service: 'Service',
  telecaller: 'TeleCaller',
  hr: 'Human Resources',
  lead_manager: 'Lead Manager',
  lead_triage: 'Lead Triage',
  super_admin: 'Platform',
  sales_executive: 'Sales Executive',
  service_technician: 'Service Technician',
  production_operator: 'Production Operator',
  warehouse_executive: 'Warehouse Executive',
  accounts_executive: 'Accounts Executive',
  field_partner: 'Field Partner',
  field_executive: 'Field Executive',
  backend_staff: 'Backend / Tele Sales',
  purchase: 'Purchase Manager',
  // Task 7 — Veda Agro wide-bundle manager roles
  veda_sales_manager: 'Sales Manager ({brand})',
  veda_logistics_manager: 'Logistics Manager ({brand})',
  veda_purchase_head: 'Purchase Head ({brand})',
  // V2 (2026-07-16) — Veda commodity sales rep
  veda_sales_executive: 'Sales Executive ({brand})',
};

/**
 * `{brand}` → the tenant's short name (lib/brand.ts): "(Veda)" on live, "(Demo)" on the demo.
 * 🔒 B43 (2026-10-12): read at USE, through a getter on every view entry. The
 * lists below are built at import, before the tenant loads; a label computed
 * there froze the fallback "(Veda)" on every tenant.
 */
const viewLabel = (v: SidebarView): string => VIEW_LABELS[v].replace('{brand}', brandTag());

/* ------------------------------------------------------------------ */
/*  Per-role sidebar configurations — D-11 Core + See-more             */
/* ------------------------------------------------------------------ */

/** Admin / Owner — D-07 core 9 + See-More */
export const OWNER_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      // 2026-09-26 (Manak): ONE item — no separate "Admin Dashboard". /dashboard lands admin +
      // owner on /veda-agro/admin (resolveVedaManagerHome); other roles that fall back to this
      // menu keep their own /dashboard, so it must stay '/dashboard', not '/veda-agro/admin'.
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/ai', icon: Sparkles, label: 'AI Insights', module: 'ai' },
    ],
  },
  {
    label: 'Revenue',
    items: [
      // Apr 27 (Kunwar BUG-15) — Leads must be reachable from Owner sidebar.
      // Was previously only in SALES_SECTIONS, invisible to admin.
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      { to: '/deals', icon: Target, label: 'Deals', module: 'crm' },
      { to: '/invoices', icon: Receipt, label: 'Invoices', module: 'finance' },
      { to: '/analytics', icon: BarChart3, label: 'Forecasting', module: 'analytics' },
    ],
  },
  {
    // Veda Agro v3 commodity deal flow — module-gated (veda_commodity_deals
    // in tenant.enabledModules; D-09: no tenant slugs in code). Triage +
    // Approvals additionally admin-only. 2026-10-07 (tour bug B-AD-01): the MD
    // desks below are admin + owner — the server lets owner use them, and an
    // MD on the owner role had lost his own desk. Routing is removed — Lead
    // Triage opens from "Leads" (vedaLeadsNav.ts).
    label: 'Commodity Deals',
    items: [
      { to: '/veda-agro/cockpit', icon: Gauge, label: 'Purchase Cockpit', module: 'veda_commodity_deals' },
      // 2026-10-07 (Manak): the separate "Routing" item is gone — admin/owner/MD
      // reach Lead Triage through "Leads" (vedaLeadsNav.ts swaps /leads for it).
      { to: '/veda-agro/enquiries', icon: FileSearch, label: VL.enquiries, module: 'veda_commodity_deals' },
      // V10 (2026-07-17) — explicit Deal Desk entry for admin. The route
      // /veda-agro/enquiries/:id → DealDeskGuard renders the Sourcing Workbench for
      // admin/owner/purchase; sales roles are redirected to their reneg page. This
      // gives admin a labelled way in (the Enquiries list above is the picker).
      { to: '/veda-agro/enquiries', icon: Handshake, label: 'Deal Desk', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      { to: '/veda-agro/approvals', icon: ShieldCheck, label: VL.approvals, module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      { to: '/veda-agro/pipeline', icon: TrendingUp, label: 'Pipeline', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      // 2026-10-07 (tour bug B-AD-02): the MD's operating desks were reachable
      // only from Admin Dashboard links. admin + owner only — this menu is also
      // the fallback for unmapped roles.
      { to: '/veda-agro/sales', icon: Target, label: 'Trading Dashboard', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      { to: '/veda-agro/stage-tracker', icon: Workflow, label: 'Dispatch Tracker', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      { to: '/veda-agro/holding-desk', icon: BadgeIndianRupee, label: 'Holding Desk', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      { to: '/veda-agro/rejected-dispatches', icon: Ban, label: 'Rejected Dispatches', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      // V4 rejected-goods: lot expenses, vendor recovery, settlement. ADMIN-ONLY to
      // start (Manak 2026-08-03) — rescope to the purchase team once it has been used.
      { to: '/veda-agro/rejection-settlements', icon: PackageX, label: 'Rejection Settlements', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      // AI-4d2e: payment advice (milestones 9/17 + security deposits). ADMIN-ONLY
      // to start, matching Rejection Settlements — it is a money-instruction
      // surface, so it is scoped narrow first and widened once the finance team
      // has used it, not the other way round.
      { to: '/veda-agro/payment-advice', icon: Banknote, label: 'Payment Advice', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      // Credit Ledger (2026-09-23) — every open plant/transporter credit
      // obligation in one list. Read-only, money-adjacent to Payment Advice
      // above it. 🔒 OWNER-ONLY FOR NOW: purchase_head and logistics_manager
      // are scope-derived via navCatalog.ts + Sidebar.scope-nav.test.ts parity
      // counts; adding this item to their hardcoded VEDA_*_SECTIONS arrays
      // without a matching navCatalog.ts entry (a new nav.* scope) breaks that
      // parity test. That scope decision is Manak's — flagged, not invented.
      { to: '/veda-agro/credit-ledger', icon: CreditCard, label: 'Credit Ledger', module: 'veda_commodity_deals' },
      // AI-xorm: weighbridge slip (milestone 14). NOT admin-only — capturing a
      // turn paper is execution work the logistics desk does, and gating it to
      // admin would just push it back to API calls, which is the state this
      // replaces.
      { to: '/veda-agro/turn-paper', icon: Scale, label: 'Turn Paper', module: 'veda_commodity_deals' },
      { to: '/veda-agro/system-health', icon: Activity, label: 'System Health', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      // Company Profile (2026-08-28) — the seller-of-record every document
      // prints from. admin + owner, matching the server's tenantAdminOnly.
      { to: '/admin/company-profile', icon: Building2, label: 'Company Profile', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
      // 🔒 The Override Desk SCREEN was retired 2026-08-27 — every repair it
      // carried (gate override, backward stage move, duplicate-document
      // removal) now lives inside the Stage Tracker's expanded deal, behind
      // the same server-side requireRole. Its BACKEND routes are untouched.
      { to: '/veda-agro/rate-update', icon: IndianRupee, label: VL.dailyRates, module: 'veda_commodity_deals' },
      { to: '/veda-agro/directory', icon: Tags, label: VL.directory, module: 'veda_commodity_deals' },
      // Customer Master (2026-07-03, Manak) — admin/owner additionally get the
      // history-guarded Delete + Deactivate controls on this page.
      { to: '/veda-agro/customers', icon: Users, label: 'Customers', module: 'veda_commodity_deals' },
      { to: '/veda-agro/work-plan', icon: Link2, label: VL.workPlan, module: 'veda_commodity_deals' },
      { to: '/veda-agro/orders', icon: Receipt, label: VL.orders, module: 'veda_commodity_deals' },
      // Delivery Pipeline (2026-09-13) — every truck still moving, with per-tab
      // money totals. 🔒 ADMIN-GATED IN THE ROUTE, not here: this board shows
      // purchase rates, vendor names and live margin on every truck in the
      // tenant, un-scoped. A nav entry hidden by the sidebar is one typed URL
      // away from being bypassed; the server refuses.
      { to: '/veda-agro/delivery-pipeline', icon: Truck, label: 'Delivery Pipeline', module: 'veda_commodity_deals' },
      // Closing Register (2026-08-30) — where a deal goes once Final Settlement
      // is advanced. Until this existed the deal vanished: the tracker query
      // excludes COMPLETED rows, so the button was a one-way door into a hole.
      // Read-only, so no role gate beyond the module.
      { to: '/veda-agro/closing-register', icon: Archive, label: 'Closing Register', module: 'veda_commodity_deals' },
    ],
  },
  {
    // Veda Agro REV3 T3 — masters parity. Module-gated (D-09: no tenant slugs).
    label: 'Masters',
    items: [
      { to: '/veda-agro/vendors', icon: Building2, label: 'Vendors', module: 'veda_commodity_deals' },
      { to: '/veda-agro/transporters', icon: Truck, label: 'Transporters', module: 'veda_commodity_deals' },
      { to: '/veda-agro/masters/godowns', icon: Warehouse, label: 'Godowns', module: 'veda_commodity_deals' },
      { to: '/veda-agro/products', icon: Package, label: 'Products', module: 'veda_commodity_deals' },
      { to: '/veda-agro/mapping', icon: Tags, label: 'Mapping', module: 'veda_commodity_deals' },
    ],
  },
  // TMS On-Route retired 2026-10-07 (logistics tour bug 13): it read the TMS
  // Shipment table, which Veda never fills. Trucks are on the Dispatch Tracker.
  // HRMS (Leaves + My Attendance) moved to the TopBar user menu under "My HR" (2026-06-17).
  {
    // Phase T8 — support ticketing. All roles.
    label: 'Support',
    items: [
      { to: '/veda-agro/resolution-centre', icon: LifeBuoy, label: 'Resolution Centre', module: 'veda_commodity_deals', roleNamesOnly: ['admin', 'owner'] },
    ],
  },
  {
    // Apr 27 (Kunwar BUG-14) — CRM customer-record surface added to Owner.
    // Sun Stellar's owner needs Contacts as a top-level group; the WhatsApp
    // lead sync from ChatKart creates Contact rows here that must be visible.
    label: 'CRM',
    items: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/customers', icon: Building2, label: 'Customers', module: 'crm' },
      { to: '/accounts', icon: FolderKanban, label: 'Accounts', module: 'accounts' },
      // v6.3.7 — Sun Stellar Pool Heat Pump Calculator. Originally placed
      // in CRM `seeMore` but the renderer only displays seeMore on the LAST
      // section (Sidebar.tsx:968 `isLast` guard), so non-last seeMore entries
      // were silently dropped. Promoted to a top-level item — only renders
      // for tenants with 'pool_heat_pump_calculator' in enabledModules.
      // Calculator itself is NOT free; this entry opens the Lead picker
      // (MeetingModeLauncher) which then binds a session.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
    seeMore: [
      { to: '/activities', icon: CalendarDays, label: 'Activities' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/sales-orders', icon: ShoppingCart, label: 'Orders', module: 'sales' },
      { to: '/manufacturing/dashboard', icon: Factory, label: 'Production', module: 'manufacturing' },
      { to: '/inventory', icon: Warehouse, label: 'Inventory', module: 'warehouse' },
      { to: '/service-tickets', icon: Wrench, label: 'Service', module: 'service-mgmt' },
    ],
    // Apr 27 (Kunwar BUG-14 + sweep) — Owner sidebar was missing most ops
    // surfaces. Adding See-More so every major function is ≤ 1 click away
    // without overcrowding the visible core.
    seeMore: [
      { to: '/projects', icon: Briefcase, label: 'Projects' },
      { to: '/tasks', icon: ClipboardCheck, label: 'Tasks' },
      { to: '/proposals', icon: FileText, label: 'Proposals', module: 'sales' },
      { to: '/estimates', icon: FileText, label: 'Estimates', module: 'sales' },
      { to: '/quotation-jobs', icon: FileText, label: 'Quotations', module: 'procurement' },
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt' },
    ],
  },
  {
    label: 'Procurement',
    items: [
      { to: '/vendors', icon: Building2, label: 'Vendors', module: 'procurement' },
      { to: '/purchase-orders', icon: ShoppingCart, label: 'Purchase Orders', module: 'procurement' },
      // Sprint 2-3 (May 24 2026 — Agent KK) — vendor bills surface
      { to: '/purchase-invoices', icon: Receipt, label: 'Purchase Invoices', module: 'procurement' },
    ],
    seeMore: [
      { to: '/purchase-dashboard', icon: LayoutDashboard, label: 'Purchase Dashboard', module: 'procurement' },
      { to: '/purchase-requests', icon: FileText, label: 'Purchase Requests', module: 'procurement' },
      { to: '/vendor-rate-comparison', icon: BarChart3, label: 'Vendor Rates', module: 'procurement' },
      { to: '/products', icon: Package, label: 'Products', module: 'warehouse' },
    ],
  },
  {
    label: 'Books',
    items: [
      { to: '/accounting?tab=ledger', icon: BookOpen, label: 'Ledger', module: 'accounting' },
      { to: '/payments', icon: CreditCard, label: 'Payments', module: 'finance' },
      { to: '/expenses', icon: Wallet, label: 'Expenses', module: 'finance' },
    ],
    seeMore: [
      // Sprint 2-3 (May 24 2026 — Agent LL) — Tally-grade ledger pack
      { to: '/ledger/vouchers', icon: ClipboardList, label: 'Voucher Register', module: 'accounting' },
      { to: '/ledger/trial-balance', icon: Scale, label: 'Trial Balance', module: 'accounting' },
      { to: '/ledger/profit-loss', icon: BarChart3, label: 'Profit & Loss', module: 'accounting' },
      { to: '/ledger/balance-sheet', icon: Calculator, label: 'Balance Sheet', module: 'accounting' },
      // [2026-07-20 payroll] role-gated to compensation audience + md
      { to: '/payroll', icon: IndianRupee, label: 'Payroll', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant', 'md'] },
      { to: '/gst/filing', icon: FileOutput, label: 'GST Filing', module: 'finance' },
      // === GSTPACK-G2B === (2026-07-19)
      { to: '/gst/gstr2b', icon: FileSearch, label: '2B Reconciliation', module: 'finance' },
      // === GSTPACK-GS9 === GSTR-9 annual return (2026-07-19)
      { to: '/gst/gstr9', icon: FileOutput, label: 'GSTR-9 Annual', module: 'finance' },
      // === GSTPACK-TLY ===
      { to: '/tally-export', icon: FileOutput, label: 'Tally Export', module: 'accounting' },
      // Deep Accounting Suite (2026-07-19 Build Plan 1 COMMON)
      { to: '/accounting/suite/reports', icon: BarChart3, label: 'Financial Reports', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
      // Payments Suite hidden (Manak 2026-10-07, B-AC-07): forms ask for raw database IDs; back when pickers exist.
      // Period Close hidden (Manak 2026-10-07, B-AC-07): forms ask for raw database IDs; back when pickers exist.
      { to: '/credit-notes', icon: FileX2, label: 'Credit Notes', module: 'finance' },
      { to: '/debit-notes', icon: ScrollText, label: 'Debit Notes', module: 'finance' },
      { to: '/payment-confirmations', icon: CheckCircle, label: 'Payment Confirmations', module: 'finance' },
      { to: '/finance/approvals', icon: ShieldCheck, label: 'Approvals', module: 'finance' },
    ],
  },
  {
    label: 'Org',
    items: [
      { to: '/employees', icon: UserCog, label: 'People', module: 'hr' },
      { to: '/telecaller', icon: Phone, label: 'Telecaller', module: 'telecaller' },
      { to: '/settings', icon: Settings, label: 'Settings' },
    ],
    // See-More for admin: deeper-ops items D-11 says live here
    seeMore: [
      { to: '/admin/settings', icon: Settings, label: 'AURA Settings' },
      { to: '/admin/carrier-numbers', icon: Phone, label: 'Carrier Numbers' },
      { to: '/admin/installations', icon: Camera, label: 'Photo Focus' },
      { to: '/settings/ai-assistant', icon: Sparkles, label: 'AI Assistant', planGate: 'ENTERPRISE' },
      // admin + owner read their own tenant's log (tour bug B-AD-03); others get 403.
      { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs', roleNamesOnly: ['admin', 'owner'] },
      { to: '/admin/login-history', icon: ScrollText, label: 'Login History' },
      { to: '/settings/sessions', icon: Smartphone, label: 'Sessions & Devices' },
      { to: '/admin/health', icon: Server, label: 'System Health' },
      { to: '/admin/resources', icon: Gauge, label: 'Resource Mgmt' },
      { to: '/admin', icon: Crown, label: 'Super Admin' },
      { to: '/workflows', icon: Workflow, label: 'Workflows', module: 'automation' },
      { to: '/credits', icon: Coins, label: 'AI Credits', module: 'ai' },
      { to: '/billing/plans', icon: IndianRupee, label: 'Billing Plans' },
      { to: '/leave-requests', icon: CalendarDays, label: 'Leave Requests', module: 'hr' },
    ],
  },
];

/** Sales Manager — D-01 + D-02 core 8 + See-More */
const SALES_SECTIONS: NavSection[] = [
  {
    label: 'Pipeline',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/deals', icon: Target, label: 'Deals', module: 'crm' },
      { to: '/analytics', icon: TrendingUp, label: 'Forecast', module: 'analytics' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { to: '/customers', icon: Building2, label: 'Customers', module: 'crm' },
      { to: '/accounts', icon: FolderKanban, label: 'Accounts', module: 'accounts' },
      { to: '/sales/activities', icon: CalendarDays, label: 'Activities' },
    ],
  },
  {
    label: 'Commodity Deals',
    items: [
      { to: '/veda-agro/cockpit', icon: Gauge, label: 'Purchase Cockpit', module: 'veda_commodity_deals' },
      { to: '/veda-agro/enquiries', icon: FileSearch, label: VL.enquiries, module: 'veda_commodity_deals' },
      { to: '/veda-agro/rate-update', icon: IndianRupee, label: VL.dailyRates, module: 'veda_commodity_deals' },
      { to: '/veda-agro/directory', icon: Tags, label: VL.directory, module: 'veda_commodity_deals' },
      { to: '/veda-agro/orders', icon: Receipt, label: VL.orders, module: 'veda_commodity_deals' },
    ],
  },
  // HRMS (Leaves + My Attendance) moved to the TopBar user menu under "My HR" (2026-06-17).
  {
    // Phase T8 — support ticketing. All roles.
    label: 'Support',
    items: [
      { to: '/veda-agro/resolution-centre', icon: LifeBuoy, label: 'Resolution Centre', module: 'veda_commodity_deals', roleNamesOnly: ['admin'] },
    ],
  },
  {
    label: 'Team',
    items: [
      { to: '/sales/reps', icon: Activity, label: 'Reps', module: 'crm' },
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      // v6.3.7 — Promoted from seeMore to top-level items so Mamta + sales.head
      // see the calculator entry without expanding See-more. Module-gated.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
    seeMore: [
      { to: '/samples', icon: FlaskConical, label: 'Samples' },
      { to: '/deal-closure', icon: Handshake, label: 'Deal Closure' },
      { to: '/discount-requests', icon: Percent, label: 'Discount Requests', module: 'sales' },
      { to: '/proposal-builder', icon: Settings, label: 'Proposal Builder', module: 'sales' },
      { to: '/proposals', icon: FileText, label: 'Proposals', module: 'sales' },
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
    ],
  },
];

/** Accountant / Account Manager — D-03 core 9 + See-More */
const ACCOUNTANT_SECTIONS: NavSection[] = [
  {
    label: 'Books',
    items: [
      // Accounts Cockpit (2026-09-25) — Veda Agro Sale Bill / Purchase Bill /
      // Final Settlement queues for the accountant. Manak 2026-09-26: this IS
      // the accountant's dashboard — the generic Dashboard item was removed.
      { to: '/veda-agro/accounts', icon: Truck, label: 'Accounts Cockpit', module: 'veda_commodity_deals' },
      // 2026-10-07 (tour bug B-AC-11): read-only Veda money views — closed
      // trucks and what is still owed to plants/transporters. Both routes are
      // authenticate-only; she could already open them by URL.
      { to: '/veda-agro/closing-register', icon: Archive, label: 'Closing Register', module: 'veda_commodity_deals' },
      { to: '/veda-agro/credit-ledger', icon: CreditCard, label: 'Credit Ledger', module: 'veda_commodity_deals' },
      { to: '/accounting?tab=ledger', icon: BookOpen, label: 'Ledger', module: 'accounting' },
      // Sprint 2-3 (May 24 2026 — Agent LL) — Vouchers now point at the new
      // dedicated Voucher Register page instead of the legacy tabbed view.
      { to: '/ledger/vouchers', icon: ClipboardList, label: 'Vouchers', module: 'accounting' },
      { to: '/invoices', icon: Receipt, label: 'Invoices', module: 'finance' },
      // Sprint 2-3 (May 24 2026 — Agent KK) — vendor bills surface
      { to: '/purchase-invoices', icon: Receipt, label: 'Purchase Invoices', module: 'finance' },
      { to: '/payment-confirmations', icon: CheckCircle, label: 'Payment Confirmations', module: 'finance' },
    ],
  },
  {
    label: 'Compliance',
    items: [
      { to: '/gst/filing', icon: FileOutput, label: 'GST Filing', module: 'finance' },
      // === GSTPACK-G2B === (2026-07-19)
      { to: '/gst/gstr2b', icon: FileSearch, label: '2B Reconciliation', module: 'finance' },
      // === GSTPACK-GS9 === GSTR-9 annual return (2026-07-19)
      { to: '/gst/gstr9', icon: FileOutput, label: 'GSTR-9 Annual', module: 'finance' },
      // === GSTPACK-TLY ===
      { to: '/tally-export', icon: FileOutput, label: 'Tally Export', module: 'accounting' },
    ],
  },
  {
    label: 'Reports',
    items: [
      // Sprint 2-3 (May 24 2026 — Agent LL) — Tally-grade reports replace
      // the legacy ?tab= aliases. Old routes still resolve (kept in seeMore
      // as fallbacks until users are off muscle memory).
      { to: '/ledger/trial-balance', icon: Scale, label: 'Trial Balance', module: 'accounting' },
      { to: '/ledger/profit-loss', icon: BarChart3, label: 'P&L', module: 'accounting' },
      { to: '/ledger/balance-sheet', icon: Calculator, label: 'Balance Sheet', module: 'accounting' },
      // [2026-07-20 payroll] role-gated to compensation audience + md
      { to: '/payroll', icon: IndianRupee, label: 'Payroll', module: 'hr', roleNamesOnly: ['owner', 'admin', 'super_admin', 'hr', 'accountant', 'md'] },
      { to: '/accounting?tab=outstanding', icon: CreditCard, label: 'Outstanding', module: 'accounting' },
      // Deep Accounting Suite (2026-07-19 Build Plan 1 COMMON)
      { to: '/accounting/suite/reports', icon: BarChart3, label: 'Financial Reports', module: 'accounting', roleNamesOnly: ['owner', 'admin', 'super_admin', 'accountant'] },
      // Payments Suite hidden (Manak 2026-10-07, B-AC-07): forms ask for raw database IDs; back when pickers exist.
      // Period Close hidden (Manak 2026-10-07, B-AC-07): forms ask for raw database IDs; back when pickers exist.
    ],
    seeMore: [
      { to: '/finance/approvals', icon: ShieldCheck, label: 'Financial Approvals', module: 'finance' },
      { to: '/finance/security-deposits', icon: Shield, label: 'Security Deposits', module: 'finance' },
      { to: '/finance/invoice-swap', icon: ArrowRightLeft, label: 'Invoice Swap', module: 'finance' },
      { to: '/credit-notes', icon: FileX2, label: 'Credit Notes', module: 'finance' },
      { to: '/debit-notes', icon: ScrollText, label: 'Debit Notes', module: 'finance' },
      { to: '/expenses', icon: Wallet, label: 'Expenses', module: 'finance' },
      { to: '/payments', icon: CreditCard, label: 'Payments', module: 'finance' },
      // Legacy tabbed reports — kept as fallbacks during the May 24 cutover
      { to: '/accounting?tab=pl', icon: BarChart3, label: 'Legacy P&L (tab)', module: 'accounting' },
      { to: '/accounting?tab=bs', icon: Calculator, label: 'Legacy Balance Sheet (tab)', module: 'accounting' },
    ],
  },
];

/** Production Manager — D-04 core 7 + See-More */
const PRODUCTION_SECTIONS: NavSection[] = [
  {
    label: 'Shop Floor',
    items: [
      { to: '/manufacturing/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'manufacturing' },
      { to: '/production', icon: Factory, label: 'Production Orders', module: 'manufacturing' },
      { to: '/manufacturing/boms', icon: Hammer, label: 'BOM Dashboard', module: 'manufacturing' },
    ],
  },
  {
    label: 'Quality',
    items: [
      { to: '/manufacturing/qc', icon: ClipboardCheck, label: 'QC', module: 'manufacturing' },
      { to: '/rejections', icon: AlertTriangle, label: 'Rejections', module: 'manufacturing' },
    ],
  },
  {
    label: 'Inputs',
    items: [
      { to: '/inventory', icon: Package, label: 'Materials', module: 'warehouse' },
      { to: '/manufacturing/process-kanban', icon: Route, label: 'Work Centres', module: 'manufacturing' },
    ],
    seeMore: [
      { to: '/manufacturing/shift-control', icon: Gauge, label: 'Shift Control', module: 'manufacturing' },
      { to: '/manufacturing/rm-inspection', icon: CheckCircle, label: 'RM Inspection', module: 'manufacturing' },
      { to: '/manufacturing/fg-activation', icon: CheckCircle, label: 'FG Activation', module: 'manufacturing' },
      { to: '/manufacturing/floor-inspection', icon: ClipboardCheck, label: 'Floor Inspection', module: 'manufacturing' },
      { to: '/manufacturing/shift-report', icon: FileText, label: 'Shift Reports', module: 'manufacturing' },
      { to: '/manufacturing/scrap', icon: Package, label: 'Scrap & Wastage', module: 'manufacturing' },
      { to: '/qc-templates', icon: ClipboardList, label: 'QC Templates', module: 'manufacturing' },
    ],
  },
];

/** Inventory Manager — D-05 core 8 + See-More */
const INVENTORY_SECTIONS: NavSection[] = [
  {
    label: 'Stock',
    items: [
      { to: '/warehouse/dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'warehouse' },
      { to: '/inventory', icon: Warehouse, label: 'Stock', module: 'warehouse' },
      { to: '/warehouse/product-units', icon: Box, label: 'Batches', module: 'warehouse' },
    ],
  },
  {
    label: 'Movements',
    items: [
      { to: '/warehouse/inward', icon: ArrowDownToLine, label: 'Inward Dashboard', module: 'warehouse' },
      { to: '/warehouse/dispatch-dashboard', icon: ArrowUpFromLine, label: 'Dispatch', module: 'warehouse' },
      { to: '/warehouse/inventory-pipeline', icon: ArrowRightLeft, label: 'Transfer', module: 'warehouse' },
    ],
  },
  {
    label: 'Warehouse',
    items: [
      { to: '/warehouse-locations', icon: MapPin, label: 'Bins', module: 'warehouse' },
      { to: '/warehouse/stock-audit', icon: ClipboardList, label: 'Cycle Count', module: 'warehouse' },
    ],
    seeMore: [
      { to: '/warehouse/barcode-onboarding', icon: ScanBarcode, label: 'Barcode Onboarding', module: 'warehouse' },
      { to: '/warehouse/barcode-lifecycle', icon: ScanBarcode, label: 'Barcode Lifecycle', module: 'warehouse' },
      { to: '/customer-returns', icon: ArrowRightLeft, label: 'Customer Returns', module: 'warehouse' },
      { to: '/warehouse/cash-rewards', icon: Gift, label: 'Cash Reward Stickers', module: 'logistics' },
      { to: '/products', icon: Package, label: 'Products', module: 'warehouse' },
      { to: '/material-boxes', icon: Box, label: 'Material Boxes', module: 'warehouse' },
      { to: '/dispatch-slips', icon: FileOutput, label: 'Dispatch Slips', module: 'warehouse' },
    ],
  },
];

/** Procurement Manager — D-06 core 6 + See-More */
const PROCUREMENT_SECTIONS: NavSection[] = [
  {
    label: 'Orders',
    items: [
      { to: '/purchase-dashboard', icon: LayoutDashboard, label: 'Dashboard', module: 'procurement' },
      { to: '/purchase-orders', icon: ShoppingCart, label: 'POs', module: 'procurement' },
      { to: '/so-po-mapping', icon: ArrowRightLeft, label: 'SO→PO Mapping', module: 'sales' },
    ],
  },
  {
    label: 'Vendors',
    items: [
      { to: '/vendors', icon: Truck, label: 'Vendors', module: 'procurement' },
      { to: '/quotation-jobs', icon: FileSearch, label: 'Quotations', module: 'procurement' },
      { to: '/vendor-rate-comparison', icon: Scale, label: 'Rate Comparison', module: 'procurement' },
    ],
    seeMore: [
      { to: '/procurement/landed-cost', icon: Calculator, label: 'Landed Cost', module: 'procurement' },
      { to: '/purchase-contracts', icon: FileSignature, label: 'Purchase Contracts', module: 'procurement' },
      { to: '/transport-contracts', icon: FileSignature, label: 'Transport Contracts', module: 'logistics' },
      { to: '/vendor-returns', icon: ArrowRightLeft, label: 'Vendor Returns', module: 'procurement' },
      { to: '/purchase-requests', icon: FileSearch, label: 'Purchase Requests', module: 'procurement' },
    ],
  },
  // Apr 28 evening — Products (master catalog) was missing entirely from
  // the Procurement view. POs reference products on every line item, so
  // procurement managers need direct access. Products page handles both
  // create + browse + edit on /products.
  {
    label: 'Catalog',
    items: [
      { to: '/products', icon: Package, label: 'Products' },
    ],
  },
];

/** Project Manager — core 7 + See-More */
const PROJECT_SECTIONS: NavSection[] = [
  {
    label: 'Delivery',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/projects', icon: FolderKanban, label: 'Projects', module: 'projects' },
      { to: '/projects?view=gantt', icon: CalendarDays, label: 'Gantt', module: 'projects' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/tasks?view=timesheets', icon: ClipboardList, label: 'Timesheets' },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/analytics?view=projects', icon: BarChart3, label: 'P&L', module: 'analytics' },
      { to: '/projects?view=milestones', icon: CheckCircle, label: 'Milestones', module: 'projects' },
    ],
    seeMore: [
      { to: '/service/itbp-installations', icon: Wrench, label: 'ITBP Installations', module: 'service-mgmt' },
      { to: '/field-partners', icon: UserCog, label: 'Field Partners Hub' },
    ],
  },
];

/** Service Manager — not in the D-11 spec but exists as a role */
const SERVICE_SECTIONS: NavSection[] = [
  {
    label: 'Service',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/service/sop-dashboard', icon: ClipboardList, label: 'SOP Dashboard', module: 'service-mgmt' },
      { to: '/service-tickets', icon: Wrench, label: 'Service Tickets', module: 'service-mgmt' },
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt' },
    ],
  },
  {
    label: 'Rules',
    items: [
      { to: '/escalation-rules', icon: AlertTriangle, label: 'Escalation Rules', module: 'service-mgmt' },
    ],
    seeMore: [
      { to: '/service/itbp-installations', icon: Wrench, label: 'ITBP Installations', module: 'service-mgmt' },
      { to: '/support/tickets', icon: Headphones, label: 'Support Desk' },
    ],
  },
];

/** Telecaller */
const TELECALLER_SECTIONS: NavSection[] = [
  {
    label: 'Calling',
    items: [
      { to: '/telecaller', icon: Phone, label: 'Today', roleNamesOnly: ['telecaller', 'admin'] },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      { to: '/offers', icon: FileSignature, label: 'Offers' },
      // Pool Heat Pump Calculator — module-gated, only renders for Sun
      // Stellar (per tenant.enabledModules). Telecallers can pull this up
      // mid-call with the customer on the line. See Track D PERSONA.md.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** HR */
const HR_SECTIONS: NavSection[] = [
  {
    label: 'People',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/employees', icon: UserCog, label: 'Employees', module: 'hr' },
      { to: '/leave-requests', icon: CalendarDays, label: 'Leave' },
      { to: '/incentives', icon: IndianRupee, label: 'Incentives', module: 'hr' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Executive sidebars (Apr 22, 2026 exec sprint — trimmed Core 3–5)   */
/* ------------------------------------------------------------------ */

/**
 * Sales Executive — Core 5 + See-More.
 *
 * Apr 28, 2026 — telecaller fully merged into this role. The previous
 * standalone TELECALLER_SECTIONS is deprecated; sales people get all the
 * calling tools (Today queue, Call Logs, My Calls, Leads, Offers) PLUS
 * their dealer/incentive/history surface. The CallBar bottom strip is
 * gated to this role + SALES_MANAGER + TELECALLER (legacy alias).
 */
const SALES_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'Calling',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/telecaller', icon: Phone, label: 'Today', roleNamesOnly: ['telecaller', 'admin', 'sales_executive'] },
      { to: '/executive/sales/call-queue', icon: Phone, label: 'Call Queue' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
      // v6.3.7 — Promoted to top-level so it doesn't get dropped by the
      // last-section-only seeMore guard (Sidebar.tsx:968). Module-gated.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
    seeMore: [
      { to: '/leads', icon: Download, label: 'Leads', module: 'lead-integrations' },
      { to: '/offers', icon: FileSignature, label: 'Offers' },
      { to: '/executive/sales/history', icon: ScrollText, label: 'Call History' },
    ],
  },
  {
    label: 'My Pipeline',
    items: [
      { to: '/executive/sales/deals', icon: Target, label: 'My Deals', module: 'crm' },
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/executive/sales/incentives', icon: IndianRupee, label: 'Incentives' },
    ],
  },
];

/** Service Technician — Core 4 + See-More */
const SERVICE_TECHNICIAN_SECTIONS: NavSection[] = [
  {
    label: 'My Route',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/service/tickets', icon: Wrench, label: 'My Tickets' },
      { to: '/executive/service/parts-expenses', icon: Package, label: 'Parts & Expenses' },
      { to: '/service-tickets', icon: ClipboardList, label: 'All Tickets', module: 'service-mgmt' },
    ],
    seeMore: [
      { to: '/warranties', icon: Shield, label: 'Warranties', module: 'service-mgmt' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
    ],
  },
];

/** Production Operator — Core 3 + See-More */
const PRODUCTION_OPERATOR_SECTIONS: NavSection[] = [
  {
    label: 'My Shift',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/production/work-orders', icon: Factory, label: 'Work Orders' },
      { to: '/executive/production/qc-checkpoints', icon: ClipboardCheck, label: 'QC Checkpoints' },
    ],
    seeMore: [
      { to: '/manufacturing/rm-inspection', icon: CheckCircle, label: 'RM Inspection', module: 'manufacturing' },
      { to: '/manufacturing/shift-report', icon: FileText, label: 'Shift Report', module: 'manufacturing' },
    ],
  },
];

/** Warehouse Executive — Core 3 + See-More */
const WAREHOUSE_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'My Floor',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/warehouse/grn', icon: ArrowDownToLine, label: 'GRN Queue' },
      { to: '/executive/warehouse/dispatch', icon: ArrowUpFromLine, label: 'My Dispatch' },
    ],
    seeMore: [
      { to: '/warehouse/stock-audit', icon: ClipboardList, label: 'Stock Audit', module: 'warehouse' },
      { to: '/warehouse/barcode-lifecycle', icon: ScanBarcode, label: 'Barcode Lifecycle', module: 'warehouse' },
    ],
  },
];

/** Accounts Executive — Core 3 + See-More */
const ACCOUNTS_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'My Desk',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/accounts/match-queue', icon: ArrowRightLeft, label: 'Match Queue' },
      { to: '/executive/accounts/approvals', icon: ShieldCheck, label: 'Approvals' },
    ],
    seeMore: [
      { to: '/expenses', icon: Wallet, label: 'Expenses', module: 'finance' },
      { to: '/payments', icon: CreditCard, label: 'Payments', module: 'finance' },
    ],
  },
];

/** Field Partner — Core 3 + See-More */
const FIELD_PARTNER_SECTIONS: NavSection[] = [
  {
    label: 'My Work',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/executive/field-partner/claims', icon: ClipboardList, label: 'My Claims' },
      { to: '/executive/field-partner/payouts', icon: Wallet, label: 'My Payouts' },
    ],
    seeMore: [
      { to: '/field-partners', icon: UserCog, label: 'Field Partners' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Apr 26, 2026 (Squad-B) — Sales role re-bifurcation                  */
/*                                                                      */
/*  SALES_EXECUTIVE_SECTIONS above is the merged legacy layout (kept    */
/*  for back-compat). The two configs below split the role into:        */
/*    - field_executive: own/assigned leads only                        */
/*    - backend_staff:   team's leads (tele sales)                      */
/* ------------------------------------------------------------------ */

/** Field Executive — sees own/assigned leads only */
const FIELD_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'My Day',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/leads', icon: Download, label: 'My Leads', module: 'lead-integrations' },
      { to: '/executive/sales/deals', icon: Target, label: 'My Deals', module: 'crm' },
      { to: '/executive/sales/call-queue', icon: Phone, label: "Today's Call Queue" },
      { to: '/calls', icon: MapPin, label: 'Visit Planner' },
    ],
    seeMore: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/call-logs', icon: ScrollText, label: 'Call History' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/leads?intent=add', icon: Download, label: 'Add Lead' },
      // Pool Heat Pump Calculator — for field execs to size pools at site
      // visits / walk-in customers. Module-gated to Sun Stellar tenant.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** Backend Staff / Tele Sales — sees the team's leads */
const BACKEND_STAFF_SECTIONS: NavSection[] = [
  {
    label: 'Calling',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/telecaller', icon: Phone, label: 'Today', roleNamesOnly: ['telecaller', 'admin', 'backend_staff', 'backend_crm', 'tele_sales'] },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
    ],
  },
  {
    label: 'Lead Pipeline',
    items: [
      { to: '/leads?scope=team', icon: Download, label: 'Team Leads', module: 'lead-integrations' },
      { to: '/leads?intent=add', icon: Download, label: 'Add Lead' },
      { to: '/deals?intent=convert', icon: Target, label: 'Convert to Deal', module: 'crm' },
    ],
    seeMore: [
      { to: '/contacts', icon: Users, label: 'Contacts', module: 'crm' },
      { to: '/tasks', icon: ListTodo, label: 'Tasks' },
      { to: '/sales/activities', icon: Activity, label: 'Activities' },
      { to: '/offers', icon: FileSignature, label: 'Quote Requests' },
      // Pool Heat Pump Calculator — backend staff / tele-sales surface.
      // Module-gated to Sun Stellar tenant.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** Lead Manager — verification + assignment workflow (Apr 26 → Apr 28, 2026)
 *  Apr 28 — re-pointed all entries to the comprehensive /lead-manager/* page
 *  set (12 screens) shipped by Squad-D. The old /leads/verified +
 *  /leads/unverified stubs from Squad-B have hardcoded "endpoint not online"
 *  fallbacks and should not be linked from any sidebar. */
const LEAD_MANAGER_SECTIONS: NavSection[] = [
  {
    label: 'Inbox',
    items: [
      { to: '/lead-manager', icon: LayoutDashboard, label: 'Dashboard' },
      { to: '/lead-manager/unverified', icon: ShieldCheck, label: 'Unverified Queue' },
      { to: '/lead-manager/verified', icon: CheckCircle, label: 'Verified Pool' },
      { to: '/lead-manager/metrics', icon: CalendarDays, label: "Today's Verifications" },
    ],
  },
  {
    label: 'Assignment',
    items: [
      { to: '/lead-manager/verified', icon: Users, label: 'Verified Leads' },
      { to: '/lead-manager/assigned', icon: Handshake, label: 'Already-Assigned Oversight' },
      { to: '/lead-manager/reassignments', icon: ScrollText, label: 'Reassignment Requests' },
    ],
    seeMore: [
      { to: '/contacts', icon: Users, label: 'All Contacts', module: 'crm' },
      { to: '/lead-manager/search', icon: FileSearch, label: 'Search' },
      { to: '/lead-manager/settings', icon: ScrollText, label: 'Lead Manager Settings' },
      // Pool Heat Pump Calculator — Lead Manager surface, useful when sizing
      // a verified lead's project before assignment. Module-gated.
      { to: '/sales/meeting/start', icon: Calculator, label: 'Initiate Meeting Mode', module: 'pool_heat_pump_calculator' },
    ],
  },
];

/** Lead Triage Coordinator (Deepika role) — May 26, 2026.
 *  STRICT view: only the triage dashboard + her own profile. No CRM, no
 *  finance, no /dashboard, no global search across contacts/deals/invoices.
 *  Permission gating is enforced server-side via requirePermission(); this
 *  sidebar mirrors it for visibility consistency. */
const LEAD_TRIAGE_SECTIONS: NavSection[] = [
  {
    label: 'Inbox',
    items: [
      { to: '/leads/triage', icon: Download, label: 'Lead Triage' },
    ],
  },
  {
    label: 'You',
    items: [
      { to: '/profile', icon: UserCog, label: 'Profile' },
    ],
  },
];

/** Super-admin — only for admin@aura.ardhanex.com */
const SUPER_ADMIN_SECTIONS: NavSection[] = [
  {
    label: 'Platform',
    items: [
      { to: '/admin', icon: Crown, label: 'Platform Dashboard' },
      { to: '/admin/tenants', icon: Building2, label: 'Tenant Management' },
      { to: '/admin/billing', icon: IndianRupee, label: 'Billing' },
      { to: '/admin/health', icon: Server, label: 'System Health' },
      { to: '/admin/team', icon: Users, label: 'Team' },
      { to: '/admin/audit-logs', icon: ScrollText, label: 'Audit Logs' },
      { to: '/admin/login-history', icon: ScrollText, label: 'Login History' },
      { to: '/admin/resources', icon: Gauge, label: 'Resources' },
      { to: '/admin/carrier-numbers', icon: Phone, label: 'Carrier Numbers' },
    ],
  },
];

/**
 * Purchase Manager (Veda REV3) — scoped sidebar. ONLY commodity-deal sourcing,
 * masters, HRMS self-service, and support. NO generic AURA modules (CRM,
 * Finance, Operations, Books, Org). Triage + Approvals stay admin-only and are
 * intentionally absent here. Dashboard + Profile reachable as always.
 */
export const PURCHASE_SECTIONS: NavSection[] = [
  {
    // Purchase Cockpit IS the overview/dashboard for this role — the generic
    // AURA Dashboard is stale here (owner-widgets, irrelevant to purchasing).
    label: 'Overview',
    items: [{ to: '/veda-agro/cockpit', icon: Gauge, label: 'Purchase Cockpit', module: 'veda_commodity_deals' }],
  },
  {
    label: 'Commodity Deals',
    items: [
      { to: '/veda-agro/enquiries', icon: FileSearch, label: VL.enquiries, module: 'veda_commodity_deals' },
      { to: '/veda-agro/enquiry-inbox', icon: TrendingUp, label: 'Renegotiate', module: 'veda_commodity_deals' },
      { to: '/veda-agro/rate-update', icon: IndianRupee, label: VL.dailyRates, module: 'veda_commodity_deals' },
      { to: '/veda-agro/directory', icon: Tags, label: VL.directory, module: 'veda_commodity_deals' },
      // Customer Master (2026-07-03, Manak) — purchase desk creates customers too.
      { to: '/veda-agro/customers', icon: Users, label: 'Customers', module: 'veda_commodity_deals' },
      { to: '/veda-agro/work-plan', icon: Link2, label: VL.workPlan, module: 'veda_commodity_deals' },
      { to: '/veda-agro/orders', icon: Receipt, label: VL.orders, module: 'veda_commodity_deals' },
    ],
  },
  {
    label: 'Masters',
    items: [
      { to: '/veda-agro/vendors', icon: Building2, label: 'Vendors', module: 'veda_commodity_deals' },
      { to: '/veda-agro/transporters', icon: Truck, label: 'Transporters', module: 'veda_commodity_deals' },
      { to: '/veda-agro/masters/godowns', icon: Warehouse, label: 'Godowns', module: 'veda_commodity_deals' },
      { to: '/veda-agro/products', icon: Package, label: 'Products', module: 'veda_commodity_deals' },
    ],
  },
  // TMS On-Route retired 2026-10-07 (logistics tour bug 13): it read the TMS
  // Shipment table, which Veda never fills. Trucks are on the Dispatch Tracker.
  // HRMS (Leaves + My Attendance) moved to the TopBar user menu under "My HR" (2026-06-17).
  {
    label: 'Support',
    items: [
      { to: '/veda-agro/resolution-centre', icon: LifeBuoy, label: 'Resolution Centre', module: 'veda_commodity_deals', roleNamesOnly: ['admin'] },
    ],
  },
];

/**
 * Task 7 (2026-06-19) — Veda Agro Sales Manager (wide bundle).
 * TaraWorld-shaped nav: Commodity Deals + Market groups.
 * Lands on /veda-agro/sales (Deal Desk / Sales Manager Dashboard).
 * WIDE: no per-step scope lockout — 1–2 operators hold many hats.
 * All items use existing routes + module: 'veda_commodity_deals' gate.
 */
/** V6: the Renegotiations dashboard nav path — single source for the badge injector. */
const RENEG_NAV_PATH = '/veda-agro/renegotiations';

export const VEDA_SALES_MANAGER_SECTIONS: NavSection[] = [
  {
    // Tightened sales-executive scope (2026-06-19, Manak): calling prefilled leads + making Sales Orders.
    // OUT of scope: Purchase Cockpit, Work Plan/Allocation, Triage, Rate Book/Daily Rates, Requirements,
    // Purchase Order, Routing, and Margin Desk/Approvals (a manager/MD review function, not the caller's job).
    label: 'Sales',
    items: [
      // 🔒 Trading Dashboard is item 1 and the landing page (Manak 2026-09-21).
      { to: '/veda-agro/sales',      icon: Target,     label: 'Trading Dashboard', module: 'veda_commodity_deals' },
      // Call List is the rep's lead reference — dialing happens in the AURA Sales app.
      { to: '/veda-agro/call-queue', icon: Phone,      label: 'Call List',         module: 'veda_commodity_deals' },
      // 2026-06-22 (Manak): "Leads"/Requirements (/veda-agro/enquiries) REMOVED from sales —
      // that page is the commodity PURCHASE-sourcing board, not a sales-rep surface.
      { to: '/veda-agro/orders',     icon: Receipt,    label: 'Sales Orders',      module: 'veda_commodity_deals' },
      // V6 (2026-07-16): MD/Purchase send-back queue with live open-count badge.
      { to: '/veda-agro/renegotiations', icon: Inbox, label: 'Renegotiations',    module: 'veda_commodity_deals' },
      // 2026-07-03 (Manak): Customer Master — sales desk creates customers
      // (delete/deactivate stays admin-only, gated on the page + backend).
      { to: '/veda-agro/customers',  icon: Users,      label: 'Customers',         module: 'veda_commodity_deals' },
      // 2026-06-22 (Manak): Stage Tracker added — VIEW ONLY (writes blocked by backend stageWriteGuard).
      // 2026-09-04 (Manak): My Pipelines — the rep's OWN trucks, read-only.
      // Its own scope, `nav.my_pipelines`, so it survives the Dispatch Tracker
      // being taken away from this role.
      { to: '/veda-agro/my-pipelines', icon: GitCommitHorizontal, label: 'My Pipelines', module: 'veda_commodity_deals' },
      // 🔒 DISPATCH TRACKER LEFT THIS NAV ON 2026-09-04. Manak: "take dispatch
      // tracker out of view and scope from sales people as well as purchase
      // people, my pipeline is enough of a view for them." It is a logistics
      // desk. This role reads its own trucks on My Pipelines above, and the
      // page itself redirects them if they reach the URL another way.
      // Margin Desk intentionally NOT here — it's an ADMIN/OWNER margin-approval function
      // (backend-gated to ADMIN/OWNER). It lives in the owner/MD view (OWNER_SECTIONS).
    ],
  },
  // 🔒 NO "Admin · Rejected Dispatches" HERE (Manak 2026-09-21): not part of
  // the sales navigation. navCatalog.ts hides it for nav.call_list holders, so
  // the scope-derived nav agrees (Sidebar.scope-nav.test.ts).
];

/**
 * V2 (2026-07-16) — Veda Sales Executive (role `sales_rep`, 13 users).
 * Commodity rep nav. Mirrors the veda_app mobile nav (Home·Customers·Enquiry·
 * Reneg), which was designed for this exact job. Calling surfaces kept (reps do
 * call); the Sun Stellar heat-pump calculator (/sales/meeting/start) is not
 * carried over — Veda sells commodities. (That item is already invisible here
 * anyway: it is gated on `pool_heat_pump_calculator`, which is NOT in Veda's
 * enabledModules — omitting it is a lock, not a behaviour change.)
 * Items module-gated purely so a future merge to `main` stays harmless —
 * `.60` is single-tenant so the gate always passes here.
 * Do NOT fold this into SALES_EXECUTIVE_SECTIONS — that is Sun Stellar's nav.
 */
export const VEDA_SALES_EXECUTIVE_SECTIONS: NavSection[] = [
  {
    label: 'Calling',
    items: [
      // 🔒 Trading Dashboard is item 1 and the landing page for sales reps too
      // (Manak 2026-09-21). It replaces the generic /dashboard link, which now
      // redirects a sales_rep here anyway (resolveVedaManagerHome).
      { to: '/veda-agro/sales', icon: Target, label: 'Trading Dashboard', module: 'veda_commodity_deals' },
      { to: '/executive/sales/call-queue', icon: Phone, label: 'Call Queue' },
      { to: '/calls', icon: Headphones, label: 'My Calls' },
      { to: '/call-logs', icon: ScrollText, label: 'Call Logs' },
    ],
  },
  {
    label: 'Trading',
    items: [
      { to: '/veda-agro/customers', icon: Users, label: 'Customers', module: 'veda_commodity_deals' },
      { to: '/veda-agro/enquiries', icon: FileSearch, label: VL.enquiries, module: 'veda_commodity_deals' },
      // V6 (2026-07-16): MD/Purchase send-back queue (plural, ≠ /renegotiate). Live open-count badge injected in Sidebar().
      { to: '/veda-agro/renegotiations', icon: Inbox, label: 'Renegotiations', module: 'veda_commodity_deals' },
      { to: '/veda-agro/my-pipelines', icon: GitCommitHorizontal, label: 'My Pipelines', module: 'veda_commodity_deals' },
      { to: '/veda-agro/orders', icon: Receipt, label: 'Sales Orders', module: 'veda_commodity_deals' },
    ],
  },
];

/**
 * Task 7 (2026-06-19) — Veda Agro Logistics Manager (wide bundle).
 * TaraWorld-shaped nav: Commodity Deals + Dispatch & Lanes groups.
 * Lands on /veda-agro/logistics (Logistics Cockpit).
 * WIDE: no per-step scope lockout — 1–2 operators hold many hats.
 */
// Phase 1 (2026-06-19, Manak): logistics_manager scope was EXACTLY three
// surfaces. That is no longer true and the old comment claiming it was left
// standing after two additions — it now carries FOUR:
//   Logistics Cockpit · Freight Desk · Trade Routes · Stage Tracker
// Purchase-owned surfaces stay out (Purchase Cockpit, Work Plan/Allocation,
// vendor Rate Book /rate-update, Directory/Trade Network, Enquiries/Deal Desk, Orders).
// Trade Routes (/veda-agro/trade-routes) re-grounded in Phase 2; Stage Tracker
// (/veda-agro/stage-tracker) in Phase 5. The Override Desk was added
// 2026-08-26 and retired 2026-08-27 — the repairs moved into the deal itself.
// 🔒 Sidebar.veda-logistics-nav.test.ts still asserts the old three-surface list
// and has been failing on Freight Desk since before this change; it is not in
// this task's scope to edit.
// Exported for the nav-scope-lock test (Sidebar.veda-logistics-nav.test.ts).
export const VEDA_LOGISTICS_MANAGER_SECTIONS: NavSection[] = [
  {
    label: 'Logistics',
    items: [
      { to: '/veda-agro/logistics',     icon: Truck,        label: 'Logistics Cockpit', module: 'veda_commodity_deals' },
      { to: '/veda-agro/freight-desk',  icon: PackageCheck, label: 'Freight Desk',      module: 'veda_commodity_deals' },
      { to: '/veda-agro/trade-routes',  icon: Route,        label: 'Trade Routes',      module: 'veda_commodity_deals' },
      // 2026-09-04 (Manak): logistics works the Allocation Board directly.
      // Rides nav.logistics_cockpit, NOT nav.allocation_board — that scope also
      // carries the Holding Desk, which logistics must not see.
      { to: '/veda-agro/work-plan',     icon: Link2,        label: 'Allocation Board',  module: 'veda_commodity_deals' },
      // 2026-09-04 (Manak): My Pipelines — the rep's OWN trucks, read-only.
      // Its own scope, `nav.my_pipelines`, so it survives the Dispatch Tracker
      // being taken away from this role.
      { to: '/veda-agro/my-pipelines', icon: GitCommitHorizontal, label: 'My Pipelines', module: 'veda_commodity_deals' },
      { to: '/veda-agro/stage-tracker', icon: Workflow,     label: 'Dispatch Tracker',     module: 'veda_commodity_deals' },
      // 2026-10-07 (Manak): Delivery Pipeline for logistics. Rides
      // nav.logistics_cockpit in navCatalog.ts (no scope reseed). The backend
      // strips purchase cost / sale value / margin for logistics (costHidden).
      { to: '/veda-agro/delivery-pipeline', icon: Truck,    label: 'Delivery Pipeline', module: 'veda_commodity_deals' },
      // 🔒 THE CLOSING REGISTER IS DELIBERATELY NOT HERE (Manak 2026-10-07: stays out). This bundle is
      // derived from the role's nav.* scopeJson on the box (see
      // Sidebar.scope-nav.test.ts), so an item added here without the matching
      // `nav.closing_register` grant on `.60` makes the hardcoded list and the
      // scoped list disagree — the entry would show for some logistics users
      // and not others. It goes in on the ship that grants the scope.
      // 🔒 The Override Desk entry stood here until 2026-08-27. Its repairs
      // moved into the Stage Tracker above, so logistics reaches them through
      // a screen it already has. LESSON KEPT: when a role-gated entry is added
      // back here, admin/owner must be listed on it too — `filterItems` tests
      // `roleNamesOnly` against the REAL `user.role.name`, which "View as"
      // does not change, so omitting them hides the entry from the one person
      // most likely to be checking it.
    ],
  },
  {
    // 🔒 PARITY WITH navCatalog.ts. This entry is gated on a scope this bundle
    // ALREADY holds, so the hardcoded list and buildScopedSections stay in
    // agreement (Sidebar.scope-nav.test.ts). Rejected Dispatches rides
    // nav.my_pipelines (navCatalog.ts) — everyone who follows trucks sees what
    // fell out of them.
    label: 'Admin',
    items: [
      { to: '/veda-agro/rejected-dispatches', icon: Ban, label: 'Rejected Dispatches', module: 'veda_commodity_deals' },
    ],
  },
];

/**
 * Phase 3 (2026-06-19) — Veda Agro Purchase Head.
 * Lands on /veda-agro/cockpit (Purchase Cockpit).
 * Nav: Deal Desk, Purchase Cockpit, Allocation Board, Orders, Stage Tracker.
 * WIDE: purchase_head is allowed into the Deal Desk (DealDeskGuard passes).
 * 2026-06-22 (Manak): + Orders (view+create SO & PO) and Stage Tracker (view-only).
 * Exported for the nav-scope test (Sidebar.veda-logistics-nav.test.ts pattern).
 */
export const VEDA_PURCHASE_HEAD_SECTIONS: NavSection[] = [
  {
    label: 'Purchase',
    items: [
      { to: '/veda-agro/cockpit',   icon: Gauge,      label: 'Purchase Cockpit', module: 'veda_commodity_deals' },
      { to: '/veda-agro/enquiries', icon: FileSearch, label: 'Deal Desk',        module: 'veda_commodity_deals' },
      { to: '/veda-agro/enquiry-inbox', icon: TrendingUp, label: 'Renegotiate', module: 'veda_commodity_deals' },
      // V6 (2026-07-16): MD/Purchase send-back queue with live open-count badge.
      { to: '/veda-agro/renegotiations', icon: Inbox, label: 'Renegotiations',   module: 'veda_commodity_deals' },
      { to: '/veda-agro/work-plan', icon: Link2,      label: 'Allocation Board', module: 'veda_commodity_deals' },
      { to: '/veda-agro/orders',    icon: Receipt,    label: 'Orders',           module: 'veda_commodity_deals' },
      // 2026-07-03 (Manak): Customer Master — purchase desk creates customers too.
      { to: '/veda-agro/customers', icon: Users,      label: 'Customers',        module: 'veda_commodity_deals' },
      // Stage Tracker — VIEW ONLY (writes blocked by backend stageWriteGuard).
      // 2026-09-04 (Manak): My Pipelines — the rep's OWN trucks, read-only.
      // Its own scope, `nav.my_pipelines`, so it survives the Dispatch Tracker
      // being taken away from this role.
      { to: '/veda-agro/my-pipelines', icon: GitCommitHorizontal, label: 'My Pipelines', module: 'veda_commodity_deals' },
      // 🔒 DISPATCH TRACKER LEFT THIS NAV ON 2026-09-04. Manak: "take dispatch
      // tracker out of view and scope from sales people as well as purchase
      // people, my pipeline is enough of a view for them." It is a logistics
      // desk. This role reads its own trucks on My Pipelines above, and the
      // page itself redirects them if they reach the URL another way.
      // Mapping Manager — vendor↔commodity master-data (purchase desk).
      { to: '/veda-agro/mapping',   icon: Tags,       label: 'Mapping',          module: 'veda_commodity_deals' },
    ],
  },
  {
    // 🔒 PARITY WITH navCatalog.ts. Rejected Dispatches rides nav.my_pipelines;
    // the Holding Desk rides nav.allocation_board, which purchase_head holds and
    // logistics_manager does not — the people raising charges must not also see
    // the desk that approves them.
    label: 'Admin',
    items: [
      { to: '/veda-agro/rejected-dispatches', icon: Ban, label: 'Rejected Dispatches', module: 'veda_commodity_deals' },
      { to: '/veda-agro/holding-desk', icon: BadgeIndianRupee, label: 'Holding Desk', module: 'veda_commodity_deals' },
    ],
  },
];

const SECTIONS_BY_VIEW: Record<SidebarView, NavSection[]> = {
  owner: OWNER_SECTIONS,
  purchase: PURCHASE_SECTIONS,
  sales: SALES_SECTIONS,
  accountant: ACCOUNTANT_SECTIONS,
  production: PRODUCTION_SECTIONS,
  inventory: INVENTORY_SECTIONS,
  procurement: PROCUREMENT_SECTIONS,
  project: PROJECT_SECTIONS,
  service: SERVICE_SECTIONS,
  telecaller: TELECALLER_SECTIONS,
  hr: HR_SECTIONS,
  lead_manager: LEAD_MANAGER_SECTIONS,
  lead_triage: LEAD_TRIAGE_SECTIONS,
  super_admin: SUPER_ADMIN_SECTIONS,
  // Executive views
  sales_executive: SALES_EXECUTIVE_SECTIONS,
  service_technician: SERVICE_TECHNICIAN_SECTIONS,
  production_operator: PRODUCTION_OPERATOR_SECTIONS,
  warehouse_executive: WAREHOUSE_EXECUTIVE_SECTIONS,
  accounts_executive: ACCOUNTS_EXECUTIVE_SECTIONS,
  field_partner: FIELD_PARTNER_SECTIONS,
  // Apr 26, 2026 (Squad-B) — bifurcated sales sub-roles
  field_executive: FIELD_EXECUTIVE_SECTIONS,
  backend_staff: BACKEND_STAFF_SECTIONS,
  // Task 7 (2026-06-19) — Veda Agro wide-bundle manager roles
  veda_sales_manager: VEDA_SALES_MANAGER_SECTIONS,
  veda_logistics_manager: VEDA_LOGISTICS_MANAGER_SECTIONS,
  // Phase 3 (2026-06-19) — Purchase Head
  veda_purchase_head: VEDA_PURCHASE_HEAD_SECTIONS,
  // V2 (2026-07-16) — Veda commodity sales rep
  veda_sales_executive: VEDA_SALES_EXECUTIVE_SECTIONS,
};

/* ------------------------------------------------------------------ */
/*  View resolver — DashboardRoleKey → SidebarView                    */
/* ------------------------------------------------------------------ */

function purchaseCockpitFirst(sections: NavSection[]): NavSection[] {
  const sectionIndex = sections.findIndex(section =>
    section.items.some(item => item.to === '/veda-agro/cockpit'));
  if (sectionIndex < 0) return sections;
  const section = sections[sectionIndex];
  const cockpitIndex = section.items.findIndex(item => item.to === '/veda-agro/cockpit');
  const reordered = {
    ...section,
    items: [section.items[cockpitIndex], ...section.items.filter((_, index) => index !== cockpitIndex)],
  };
  return [reordered, ...sections.filter((_, index) => index !== sectionIndex)];
}

function roleKeyToView(roleKey: DashboardRoleKey, rawRoleName?: string): SidebarView {
  // 2026-10-07 (logistics tour bug 20): logistics_head / LOGISTICS = logistics_manager.
  const roleName = rawRoleName === undefined ? undefined : asLogisticsRole(rawRoleName);
  // Tenant admin (owner) → owner (D-07)
  if (roleName === 'admin') return 'owner';
  // Veda REV3 — Purchase Manager: scoped commodity-deal view (no generic AURA nav).
  if (roleName === 'purchase' || roleName === 'purchase_manager') return 'purchase';
  // Task 7 (2026-06-19) — Veda Agro wide-bundle manager roles.
  // Resolved by role.name BEFORE the formal roleKey switch so the DB-stored
  // role name wins regardless of department/designation. WIDE bundles: no
  // per-step scope lockout. sales_manager lands on /veda-agro/sales;
  // logistics_manager lands on /veda-agro/logistics.
  if (roleName === 'sales_manager') return 'veda_sales_manager';
  if (roleName === 'logistics_manager') return 'veda_logistics_manager';
  if (roleName === 'purchase_head') return 'veda_purchase_head';
  // V2 (2026-07-16) — Veda commodity sales rep. Resolved by role.name BEFORE the
  // SALES_EXECUTIVE roleKey branch below, which would otherwise hand a Veda rep
  // the Sun Stellar sales-executive nav.
  if (roleName === 'sales_rep') return 'veda_sales_executive';
  // May 26, 2026 — Lead Triage Coordinator (Deepika). Strict-view gate.
  // Resolved BEFORE every other check so it wins for the dedicated role.
  if (roleName === 'lead_triage_coordinator') return 'lead_triage';
  if ((roleKey as string) === 'LEAD_TRIAGE_COORDINATOR') return 'lead_triage';
  // Apr 26, 2026 (Squad-B) — LEAD_MANAGER + BACKEND_STAFF resolve from
  // role.name BEFORE the formal roleKey switch, since DashboardRoleKey may
  // not have been widened by Squad-A yet at compile time.
  if (roleName === 'lead_manager') return 'lead_manager';
  if (roleName === 'backend_staff' || roleName === 'backend_crm' || roleName === 'tele_sales') return 'backend_staff';
  if (roleName === 'field_executive') return 'field_executive';
  // String-cast roleKey check so newer enum values (added by Squad-A) work.
  if ((roleKey as string) === 'LEAD_MANAGER') return 'lead_manager';
  if ((roleKey as string) === 'BACKEND_STAFF') return 'backend_staff';
  // Finance manager / accountant → accountant
  if (roleKey === 'FINANCE_MANAGER') return 'accountant';
  // Sales Manager / Head → sales
  if (roleKey === 'SALES_HEAD' || roleKey === 'SALES_MANAGER') return 'sales';
  // Sales Executive → sales_executive
  // (Apr 24, 2026 — field/office merged into a single SALES_EXECUTIVE key.)
  if (roleKey === 'SALES_EXECUTIVE') return 'sales_executive';
  // Production Head → production
  if (roleKey === 'PRODUCTION_HEAD') return 'production';
  // Production Supervisor / Operator → production_operator
  if (roleKey === 'PRODUCTION_SUPERVISOR') return 'production_operator';
  // Inventory
  if (roleKey === 'INVENTORY_HEAD') return 'inventory';
  // Procurement
  if (roleKey === 'PURCHASE_HEAD') return 'procurement';
  // Service Manager → service (manager sidebar)
  if (roleKey === 'SERVICE_MANAGER') return 'service';
  // Service Technician → service_technician (trimmed exec sidebar)
  if (roleKey === 'SERVICE_TECHNICIAN') return 'service_technician';
  // Telecaller → sales_executive (shares call-queue-driven workflow; legacy telecaller sidebar kept for admin override)
  if (roleKey === 'TELECALLER') return 'sales_executive';
  // Warehouse Executive → warehouse_executive (trimmed exec sidebar)
  if (roleKey === 'WAREHOUSE_EXECUTIVE') return 'warehouse_executive';
  // Accounts Executive → accounts_executive (trimmed exec sidebar)
  if (roleKey === 'ACCOUNTS_EXECUTIVE') return 'accounts_executive';
  // Field Partner → field_partner (trimmed exec sidebar)
  if (roleKey === 'FIELD_PARTNER') return 'field_partner';
  // Owner — default
  return 'owner';
}

/** Test-only export of the resolver (V2, 2026-07-16). Not for app use. */
export const __roleKeyToViewForTest = roleKeyToView;

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Filter an item list by enabled modules and role-name gates. */
const PLAN_ORDER: Record<string, number> = {
  FREE: 0, STARTER: 1, GROWTH: 2, ENTERPRISE: 3,
};

function filterItems(
  items: NavItem[],
  enabledModules: Set<string> | null,
  roleName: string,
  planTier: string | null,
): NavItem[] {
  return items.filter((item) => {
    if (item.roleNamesOnly && !item.roleNamesOnly.includes(asLogisticsRole(roleName))) return false;
    if (item.module && enabledModules && !enabledModules.has(item.module)) return false;
    if (item.planGate) {
      const tenantLevel = PLAN_ORDER[planTier ?? 'FREE'] ?? 0;
      const requiredLevel = PLAN_ORDER[item.planGate] ?? 0;
      if (tenantLevel < requiredLevel) return false;
    }
    return true;
  });
}

/**
 * Test-only exports (2026-08-25, originally the Override Desk nav lock; that
 * screen was retired 2026-08-27 but these stay — `filterItems` is the ONLY
 * thing standing between any role-gated nav item and every role, so a
 * nav-visibility test must go through it rather than reading the raw section
 * arrays). Not for app use.
 */
export const __filterItemsForTest = filterItems;
export const __sectionsByViewForTest = SECTIONS_BY_VIEW;

/**
 * Pick the section list the sidebar draws: the scope menu (navCatalog) when
 * the user carries scopes and the role is not exempt, else the role menu.
 */
export function composeBaseSections({
  roleName,
  isSuperAdmin,
  scopes,
  activeView,
}: {
  roleName: string;
  isSuperAdmin: boolean;
  scopes?: string[] | null;
  activeView: SidebarView;
}): NavSection[] {
  // 🔒 accountant is exempt too (tour bug B-AC-01, 2026-10-07): navCatalog
  // holds only Veda commodity items, so a scoped accountant got an EMPTY menu.
  // Her work is the generic books + Accounts Cockpit — ACCOUNTANT_SECTIONS.
  const isScopeExempt = isSuperAdmin || roleName === 'admin' || roleName === 'owner' || roleName === 'accountant';
  const built =
    !isScopeExempt && scopes && scopes.length > 0
      ? buildScopedSections(scopes, roleName)
      : null;
  // Scopes that grant no menu item must not draw an empty sidebar ([] is truthy).
  const scopedSections = built && built.length > 0 ? built : null;
  const purchaseRole = roleName === 'purchase' || roleName === 'purchase_head' || roleName === 'purchase_manager';
  return scopedSections
    ? (purchaseRole ? purchaseCockpitFirst(scopedSections) : scopedSections)
    : (SECTIONS_BY_VIEW[activeView] ?? OWNER_SECTIONS);
}

/**
 * App Tour (2026-10-10, ported from Sun Stellar): every role's menu ends with
 * Help → App Tour, drawn after the role / scope sections so it never depends on
 * which menu the role gets and never moves the "See more" drawer.
 */
const APP_TOUR_NAV_ITEM: NavItem = { to: '/tour', icon: GraduationCap, label: 'App Tour' };

/** Strip any query-string for NavLink matching (we pass `?tab=ledger` in `to`). */
function routeOnly(to: string): string {
  const q = to.indexOf('?');
  return q === -1 ? to : to.slice(0, q);
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export interface SidebarProps {
  open: boolean;
  onToggle: () => void;
  /**
   * Optional view override from TopBar dept-dropdown (admin-only).
   * If set, takes priority over the user's resolved role.
   */
  viewOverride?: SidebarView | null;
  /**
   * Mobile only — called after a nav link tap so the drawer closes.
   * Wired by AppShell when rendering the mobile drawer overlay.
   */
  onMobileClose?: () => void;
}

export function Sidebar({ open, onToggle: _onToggle, viewOverride, onMobileClose }: SidebarProps) {
  const { user, tenant } = useAuthStore();
  const { config: fpConfig, enabled: fpEnabled } = useFieldProgramConfig();

  const roleName = (user?.role?.name ?? '').toLowerCase();
  const isSuperAdmin =
    user?.email === 'admin@aura.ardhanex.com' || user?.email === 'admin@aura.ardhanex.info';
  const roleKey = useMemo(() => resolveRoleKey(user as never), [user]);

  // Determine active view — super-admin > explicit override > role-resolved
  const activeView: SidebarView = isSuperAdmin
    ? 'super_admin'
    : (viewOverride ?? roleKeyToView(roleKey, roleName));

  const enabledModules = useMemo<Set<string> | null>(() => {
    if (!tenant?.enabledModules) return null; // null = all enabled
    return new Set(tenant.enabledModules);
  }, [tenant]);

  // See-more drawer state — persisted per-role
  const moreStorageKey = `aura-sidebar-more-${activeView}`;
  const [seeMoreOpen, setSeeMoreOpen] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(moreStorageKey) === '1';
  });
  useEffect(() => {
    // Re-load on view change
    if (typeof window !== 'undefined') {
      setSeeMoreOpen(localStorage.getItem(moreStorageKey) === '1');
    }
  }, [moreStorageKey]);
  const toggleSeeMore = () => {
    setSeeMoreOpen((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem(moreStorageKey, next ? '1' : '0');
      }
      return next;
    });
  };

  // Compose sections — Phase 4 (2026-06-20): when flag ON the backend populates
  // user.scopes; we derive sections from the nav catalog filtered by those scopes.
  // Flag OFF (default/deploy state): user.scopes is absent/empty → hardcoded path
  // identical to today. The hardcoded arrays are NEVER deleted — they are the
  // permanent fallback.
  //
  // Exemption rule (Phase 4): super-admin, 'admin', and 'owner' ALWAYS use the
  // hardcoded full nav, regardless of scopes. Reason: scope-derived nav only
  // covers the 10 Veda commodity items; admin/owner need the whole platform
  // (CRM, Finance, HR, Settings, …). Putting them through buildScopedSections
  // would silently hide the rest of the platform when the flag is ON.
  // roleName is already lower-cased (line 1105).
  // Release 2026-10-07: accounts-admin's composeBaseSections (accountant exempt,
  // empty-scope fallback) picks the menu; Lead Triage then overlays "Leads".
  const rawSections: NavSection[] = composeBaseSections({
    roleName,
    isSuperAdmin,
    scopes: user?.scopes,
    activeView,
  });
  // Lead Triage (2026-10-07): Veda admin/owner/MD — "Leads" opens /veda-agro/triage.
  // Non-Veda tenants get rawSections back untouched. While an admin PREVIEWS
  // another role's menu (viewOverride), show that role's menu as-is.
  const previewingOtherRole = !!viewOverride && viewOverride !== 'owner';
  const baseSections = previewingOtherRole
    ? rawSections
    : applyVedaLeadsNav(rawSections, { roleName, enabledModules, triageItem: { icon: ClipboardList } });

  const planTier = tenant?.planTier ?? null;

  // V6 (2026-07-16): live open-count badge for the Renegotiations dashboard.
  // Only polls for views that actually carry the reneg nav item (the 3 Veda navs),
  // so it never fires on Sun Stellar / non-Veda tenants. Refetches on interval +
  // window focus so a new send-back shows a number without opening the page.
  const showReneg = useMemo(
    () =>
      baseSections.some((s) =>
        [...s.items, ...(s.seeMore ?? [])].some((i) => i.to === RENEG_NAV_PATH),
      ),
    [baseSections],
  );
  const [renegCount, setRenegCount] = useState(0);
  useEffect(() => {
    if (!showReneg) {
      setRenegCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await api.get('/veda-agro/renegotiations/count', { params: { status: 'open' } });
        if (!cancelled) setRenegCount(Number(res.data?.count ?? 0));
      } catch {
        /* badge is best-effort — never break the sidebar on a count failure */
      }
    };
    void fetchCount();
    const interval = window.setInterval(fetchCount, 60_000);
    const onFocus = () => void fetchCount();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [showReneg]);

  // Sprint DDN (2026-07-19): live "unseen MD decisions" badge on the admin
  // Deal Desk nav item. Only polls for views that actually carry a Deal Desk
  // nav item (mirrors the Renegotiations pattern above). Refetches on interval
  // + window focus so a fresh MD decision shows a number without opening the
  // page. Fail-soft: a count fetch error leaves the badge at 0.
  const showDealDesk = useMemo(
    () =>
      baseSections.some((s) =>
        [...s.items, ...(s.seeMore ?? [])].some((i) => shouldBadgeDealDesk(i, 1)),
      ),
    [baseSections],
  );
  const [dealDecisionCount, setDealDecisionCount] = useState(0);
  useEffect(() => {
    if (!showDealDesk) {
      setDealDecisionCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await api.get('/veda-agro/deal-desk/decisions/unseen-count');
        if (!cancelled) setDealDecisionCount(Number(res.data?.data?.count ?? 0));
      } catch {
        /* badge is best-effort — never break the sidebar on a count failure */
      }
    };
    void fetchCount();
    const interval = window.setInterval(fetchCount, 60_000);
    const onFocus = () => void fetchCount();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [showDealDesk]);

  // Lead Triage (2026-10-07): red count of unassigned leads on the Veda "Leads"
  // item. Only admin/owner/MD on a Veda tenant poll — the endpoint 403s for
  // everyone else, and Sun Stellar never calls it.
  const showTriage = useMemo(
    () =>
      canSeeLeadTriage(roleName, enabledModules) &&
      baseSections.some((s) => [...s.items, ...(s.seeMore ?? [])].some((i) => i.to === LEAD_TRIAGE_PATH)),
    [baseSections, roleName, enabledModules],
  );
  const [triageCount, setTriageCount] = useState(0);
  useEffect(() => {
    if (!showTriage) {
      setTriageCount(0);
      return;
    }
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const res = await api.get(LEAD_TRIAGE_SUMMARY_URL);
        if (!cancelled) setTriageCount(Number(res.data?.data?.unassigned ?? 0));
      } catch {
        /* badge is best-effort — never break the sidebar on a count failure */
      }
    };
    void fetchCount();
    const interval = window.setInterval(fetchCount, 60_000);
    const onFocus = () => void fetchCount();
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [showTriage]);

  const sections = useMemo<NavSection[]>(() => {
    const withCount = (item: NavItem): NavItem => {
      if (item.to === LEAD_TRIAGE_PATH && triageCount > 0) {
        return { ...item, count: triageCount, countTone: 'alert' };
      }
      if (item.to === RENEG_NAV_PATH && renegCount > 0) {
        return { ...item, count: renegCount };
      }
      if (shouldBadgeDealDesk(item, dealDecisionCount)) {
        return { ...item, count: dealDecisionCount };
      }
      return item;
    };
    return baseSections
      .map((section) => {
        const core = filterItems(section.items, enabledModules, roleName, planTier).map(withCount);
        const more = section.seeMore
          ? filterItems(section.seeMore, enabledModules, roleName, planTier).map(withCount)
          : undefined;
        return { label: section.label, items: core, seeMore: more };
      })
      .filter((s) => s.items.length > 0 || (s.seeMore && s.seeMore.length > 0));
  }, [baseSections, enabledModules, roleName, planTier, renegCount, dealDecisionCount, triageCount]);

  // TODO(D-09 tenant-leak, Apr 22 2026): Dynamic "Sun Stellar Plumber Connect"
  // section hid from sidebar because fpConfig.appName leaks tenant brand across
  // roles. Field-Partner routes remain reachable via Settings → Modules →
  // Field Partners (gated by enabledModules) and via direct URL. Revisit with
  // a proper role-gated + module-gated rework. Do NOT re-enable without that.
  // See docs/ui-rebuild/DEFERRED-FIELD-PARTNERS-SIDEBAR.md for the full plan.
  // Note: useFieldProgramConfig() hook call above intentionally kept — other
  // consumers (FieldProgramGate, field-partner pages) depend on it.
  const fieldPartnersSection: NavSection | null = null;

  const allSections = fieldPartnersSection ? [...sections, fieldPartnersSection] : sections;

  // Last section — attach see-more chevron here if drawer exists
  const lastSectionIdx = allSections.length - 1;

  return (
    <aside
      className={`flex flex-col h-full min-h-0 border-r transition-all duration-200 ${
        open ? 'w-60' : 'w-16'
      }`}
      style={{
        background: 'var(--ivory-1)',
        borderColor: 'var(--line)',
        flexShrink: 0,
      }}
    >
      {/* Role tag header */}
      <div
        className="flex items-center gap-2 px-3 pt-3 pb-2"
        style={{ color: 'var(--ink-3)' }}
      >
        {open && (
          <span
            className="text-[10px] font-medium uppercase tracking-wider"
          >
            {viewLabel(activeView)}
          </span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-2 pb-2">
        {allSections.map((section, sIdx) => {
          const isLast = sIdx === lastSectionIdx;
          const hasSeeMore = isLast && section.seeMore && section.seeMore.length > 0;
          return (
            <div key={section.label} className="mb-3">
              {open && (
                <p
                  className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider"
                  style={{ color: 'var(--ink-4)' }}
                >
                  {section.label}
                </p>
              )}
              {section.items.map((item) => (
                <SidebarLink key={`${section.label}-${item.to}`} item={item} collapsed={!open} onMobileClose={onMobileClose} />
              ))}
              {hasSeeMore && (
                <div className="mt-1">
                  {open && (
                    <button
                      type="button"
                      onClick={toggleSeeMore}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors"
                      style={{
                        color: 'var(--ink-3)',
                        background: 'transparent',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ivory-2)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      {seeMoreOpen
                        ? <ChevronDown size={13} />
                        : <ChevronRight size={13} />
                      }
                      <span>{seeMoreOpen ? 'See less' : 'See more'}</span>
                    </button>
                  )}
                  {seeMoreOpen && section.seeMore!.map((item) => (
                    <SidebarLink
                      key={`more-${item.to}`}
                      item={item}
                      collapsed={!open}
                      dimmed
                      onMobileClose={onMobileClose}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
        <div className="mb-3">
          {open && (
            <p
              className="mb-1 px-2 text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: 'var(--ink-4)' }}
            >
              Help
            </p>
          )}
          <SidebarLink item={APP_TOUR_NAV_ITEM} collapsed={!open} onMobileClose={onMobileClose} />
        </div>
      </nav>

      {/* AI summary card footer — only in expanded mode, and hidden for
          strict-view roles that don't have AI panel access (May 26, 2026). */}
      {open && activeView !== 'lead_triage' && (
        <div
          className="m-2 rounded-md border p-3"
          style={{
            background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
            borderColor: 'var(--saffron-100)',
          }}
        >
          <div className="mb-1 flex items-center gap-1.5" style={{ color: 'var(--saffron-600)' }}>
            <Sparkles size={12} />
            <span className="text-[11px] font-semibold">Weekly AI summary</span>
          </div>
          <p className="text-[11px] leading-snug" style={{ color: 'var(--ink-2)' }}>
            Open the AI panel (⌘J) for insights tailored to your role.
          </p>
        </div>
      )}
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/*  Link                                                                */
/* ------------------------------------------------------------------ */

function SidebarLink({
  item,
  collapsed,
  dimmed = false,
  onMobileClose,
}: {
  item: NavItem;
  collapsed: boolean;
  dimmed?: boolean;
  onMobileClose?: () => void;
}) {
  const IconCmp = item.icon;
  const route = routeOnly(item.to);
  return (
    <NavLink
      to={item.to}
      end={route === '/dashboard'}
      title={collapsed ? item.label : undefined}
      onClick={onMobileClose}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-md px-2 py-1.5 text-[13px] transition-colors my-0.5 ${
          collapsed ? 'justify-center' : ''
        }`
      }
      style={({ isActive }) => ({
        background: isActive ? 'var(--ivory-2)' : 'transparent',
        color: isActive ? 'var(--ink-0)' : dimmed ? 'var(--ink-3)' : 'var(--ink-1)',
        fontWeight: isActive ? 500 : 400,
        position: 'relative',
      }) as React.CSSProperties}
      onMouseEnter={(e) => {
        if (!e.currentTarget.classList.contains('active')) {
          e.currentTarget.style.background = 'var(--ivory-2)';
        }
      }}
      onMouseLeave={(e) => {
        // Preserve active state on leave via style recompute — NavLink re-renders anyway
        const isActive = e.currentTarget.getAttribute('aria-current') === 'page';
        if (!isActive) e.currentTarget.style.background = 'transparent';
      }}
    >
      <IconCmp size={15} style={{ flexShrink: 0 }} />
      {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
      {!collapsed && typeof item.count === 'number' && (
        <span
          data-testid="nav-count"
          data-tone={item.countTone ?? 'neutral'}
          className="rounded-full px-1.5 text-[10px] tabular-nums"
          style={item.countTone === 'alert'
            ? { background: 'var(--danger)', color: 'var(--ivory-0)', fontWeight: 700 }
            : { background: 'var(--ivory-2)', color: 'var(--ink-3)' }}
        >
          {item.count}
        </span>
      )}
    </NavLink>
  );
}

/* ------------------------------------------------------------------ */
/*  Named helpers (for TopBar dept-dropdown)                           */
/* ------------------------------------------------------------------ */

export const SIDEBAR_VIEWS: { id: SidebarView; label: string }[] = [
  // Managers
  { id: 'owner', get label() { return viewLabel('owner'); } },
  { id: 'sales', get label() { return viewLabel('sales'); } },
  { id: 'lead_manager', get label() { return viewLabel('lead_manager'); } },
  { id: 'lead_triage', get label() { return viewLabel('lead_triage'); } },
  { id: 'accountant', get label() { return viewLabel('accountant'); } },
  { id: 'production', get label() { return viewLabel('production'); } },
  { id: 'inventory', get label() { return viewLabel('inventory'); } },
  { id: 'procurement', get label() { return viewLabel('procurement'); } },
  { id: 'project', get label() { return viewLabel('project'); } },
  { id: 'service', get label() { return viewLabel('service'); } },
  { id: 'telecaller', get label() { return viewLabel('telecaller'); } },
  { id: 'hr', get label() { return viewLabel('hr'); } },
  // Executives (Apr 22, 2026 exec sprint)
  { id: 'sales_executive', get label() { return viewLabel('sales_executive'); } },
  { id: 'field_executive', get label() { return viewLabel('field_executive'); } },
  { id: 'backend_staff', get label() { return viewLabel('backend_staff'); } },
  { id: 'service_technician', get label() { return viewLabel('service_technician'); } },
  { id: 'production_operator', get label() { return viewLabel('production_operator'); } },
  { id: 'warehouse_executive', get label() { return viewLabel('warehouse_executive'); } },
  { id: 'accounts_executive', get label() { return viewLabel('accounts_executive'); } },
  { id: 'field_partner', get label() { return viewLabel('field_partner'); } },
  // Task 7 (2026-06-19) — Veda Agro wide-bundle manager views
  { id: 'veda_sales_manager', get label() { return viewLabel('veda_sales_manager'); } },
  { id: 'veda_logistics_manager', get label() { return viewLabel('veda_logistics_manager'); } },
  // Phase 3 (2026-06-19) — Purchase Head
  { id: 'veda_purchase_head', get label() { return viewLabel('veda_purchase_head'); } },
];

/**
 * Grouped views for TopBar's split "View as" dropdown (Apr 22, 2026 exec sprint).
 * TopBar renders these as two sections with headings: MANAGERS / EXECUTIVES.
 */
export const SIDEBAR_VIEW_GROUPS: {
  heading: string;
  views: { id: SidebarView; label: string }[];
}[] = [
  {
    heading: 'MANAGERS',
    views: [
      { id: 'owner', get label() { return viewLabel('owner'); } },
      { id: 'sales', get label() { return viewLabel('sales'); } },
      // Apr 26 2026 (Squad-B) — Lead Manager added between Sales and Accountant.
      { id: 'lead_manager', get label() { return viewLabel('lead_manager'); } },
      // May 26 2026 — Lead Triage Coordinator (Deepika) — strict-view role.
      { id: 'lead_triage', get label() { return viewLabel('lead_triage'); } },
      { id: 'accountant', get label() { return viewLabel('accountant'); } },
      { id: 'production', get label() { return viewLabel('production'); } },
      { id: 'inventory', get label() { return viewLabel('inventory'); } },
      { id: 'procurement', get label() { return viewLabel('procurement'); } },
      { id: 'project', get label() { return viewLabel('project'); } },
      { id: 'service', get label() { return viewLabel('service'); } },
      { id: 'hr', get label() { return viewLabel('hr'); } },
      // Apr 28 2026 — TeleCaller removed from MANAGERS group; merged into
      // Sales Executive role (telecaller is a sales-exec function, not a
      // manager role). Telecaller calling features now live inside the
      // Sales Executive sidebar (Today / Call Logs / My Calls / Leads / Offers).
      // Task 7 (2026-06-19) — Veda Agro wide-bundle manager roles.
      { id: 'veda_sales_manager', get label() { return viewLabel('veda_sales_manager'); } },
      { id: 'veda_logistics_manager', get label() { return viewLabel('veda_logistics_manager'); } },
      // Phase 3 (2026-06-19) — Purchase Head
      { id: 'veda_purchase_head', get label() { return viewLabel('veda_purchase_head'); } },
    ],
  },
  {
    heading: 'EXECUTIVES',
    views: [
      { id: 'sales_executive', get label() { return viewLabel('sales_executive'); } },
      // Apr 26 2026 (Squad-B) — sales executive bifurcation.
      { id: 'field_executive', get label() { return viewLabel('field_executive'); } },
      { id: 'backend_staff', get label() { return viewLabel('backend_staff'); } },
      { id: 'service_technician', get label() { return viewLabel('service_technician'); } },
      { id: 'production_operator', get label() { return viewLabel('production_operator'); } },
      { id: 'warehouse_executive', get label() { return viewLabel('warehouse_executive'); } },
      { id: 'accounts_executive', get label() { return viewLabel('accounts_executive'); } },
      { id: 'field_partner', get label() { return viewLabel('field_partner'); } },
    ],
  },
];
